import React from 'react';
import { partition } from 'lodash';
import { isOutDatedDevice } from './generate-color-and-text';
import columns, { type Device, type Org } from './columns';
import Table, { useTable, InitialTableState } from '../components/table';
import useReduceTableState from './hooks/useReduceTableState';

interface Props {
  readonly devices: Device[];
  readonly org: Org;
}

enum authMigrationEndDates {
  'phase1' = 'August 29th, 2024',
  'phase2' = 'September 26th, 2024',
  'phase3' = 'September 30th, 2024',
}

function getAuthDate(planName: string) {
  const phase1Plans = [
    'ASSP Starter',
    'Starter (FPGD)',
    'Starter V2',
    'Starter',
  ];
  const phase2Plans = [
    'Professional (Variable)',
    'Professional 2019',
    'Professional',
  ];
  const phase3Plans = [
    'Community',
    'Developer',
    'Education',
    'Enterprise (Variable)',
    'Enterprise',
    'Parter - Professional',
    'Parter - Training',
    'Partner - Enterprise',
    'Partner - Trial (AddOns)',
    'Partner - Trial (Sitemark)',
    'Partner - Trial',
    'Partner (Legacy)',
    'Partner Customer',
    'Partner',
  ];

  let planPhase = 'phase3'; // Default to last phase if plan is not found
  if (phase1Plans.includes(planName)) {
    planPhase = 'phase1';
  } else if (phase2Plans.includes(planName)) {
    planPhase = 'phase2';
  } else if (phase3Plans.includes(planName)) {
    // Keeping this to be explicit, default to 'phase3'
  }

  return authMigrationEndDates[planPhase];
}

const AuthenticationText = ({ text }: { text: string}) => (
  <p style={{ fontWeight: 600, margin: '24px 0 18px 0' }}>
    {text}
  </p>
);

const initialState = {
  ...InitialTableState,
  sort: 'email',
  perPage: 20000,
};

export default function AuthenticationCompliance({ org, devices }: Props) {
  const [outdated, unauthenticated] = partition(devices,
    (device) => isOutDatedDevice(device.fulcrum_version, device.platform));

  const { handlers: outDatedHandlers, ...outDatedTableState } = useTable(initialState);
  const { handlers: unAuthenticatedHandlers, ...unAuthenticatedTableState } = useTable(initialState);
  const { ...sortedDevices } = useReduceTableState(
    { outdated, unauthenticated }, unAuthenticatedTableState, outDatedTableState,
  );

  return (
    <section>
      <h1
        style={{
          fontWeight: 'bold',
          fontSize: 24,
          margin: '24px 0 18px 0',
        }}
      >
        {org.name}
      </h1>
      <p>
        In an effort to enhace security for mobile devices, Fulcrum has
        instituted a new authentication method. This will require users to
        re-authenticate by logging out and back in by
        {' '}
        <span style={{ fontWeight: 'bold' }}>{getAuthDate(org.plan)}</span>
      </p>
      {!!outdated.length && (
        <>
          <AuthenticationText text="These devices require updating Fulcrum prior to re-authentication:" />
          <Table
            data={sortedDevices.outdated}
            columns={columns}
            tableState={outDatedTableState}
            isLoading={false}
            isError={false}
            onSort={outDatedHandlers.sort}
          />
        </>
      )}
      <AuthenticationText text="These devices require re-authentication. Please sign out and sign back in:" />
      <Table
        data={sortedDevices.unauthenticated}
        columns={columns}
        tableState={unAuthenticatedTableState}
        isLoading={false}
        isError={false}
        onSort={unAuthenticatedHandlers.sort}
      />
    </section>
  );
}
